<template>
  <v-dialog v-model="show" max-width="800px">
    <v-card>
      <v-card-title>
        個別ファイル出力
      </v-card-title>

      <v-card-text class="px-5 mt-6">
        <!-- ダウンロードボタン -->
        <div class="mb-4">
          <v-btn color="green" dark class="mr-2 mb-2" @click="downloadQuestionWord">
            問題用紙Word出力
          </v-btn>
          <v-btn color="green" dark class="mr-2 mb-2" @click="downloadAnswerSheetExcel">
            解答用紙Excel出力
          </v-btn>
          <v-btn color="green" dark class="mb-2" @click="downloadAnswerExcel">
            解答Excel出力
          </v-btn>
        </div>

        <!-- テーブル -->
        <v-simple-table v-if="isKokugoOrEigo">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left pl-1 py-2 th_top">大問番号</th>
                <th class="text-left pl-1 py-2 th_top">音声ファイル</th>
                <th class="text-left pl-1 py-2 th_top">スクリプト画像</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="content in sheetContentPages" :key="content.number">
                <td class="text-left pl-1 py-2 td_top">{{content.number}}</td>
                <td class="text-left pl-1 py-2 td_top">
                  <v-progress-circular
                    v-if="isLoadingAudio(content)"
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  ></v-progress-circular>
                  <template v-else-if="getAudioFiles(content).length > 0">
                    <v-btn
                      v-for="file in getAudioFiles(content)"
                      :key="file.index"
                      color="secondary"
                      small
                      class="mr-1 mb-1"
                      @click="downloadAudioFile(content, file.index)"
                    >{{ file.index }}</v-btn>
                  </template>
                  <template v-else>該当ファイル無し</template>
                </td>
                <td class="text-left pl-1 py-2 td_top">
                  <v-progress-circular
                    v-if="isLoadingScript(content)"
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  ></v-progress-circular>
                  <template v-else-if="getScriptFiles(content).length > 0">
                    <v-btn
                      v-for="file in getScriptFiles(content)"
                      :key="file.index"
                      color="secondary"
                      small
                      class="mr-1 mb-1"
                      @click="downloadScriptImage(content, file.index)"
                    >{{ file.index + 1 }}</v-btn>
                  </template>
                  <template v-else>該当ファイル無し</template>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>

      <v-card-actions class="card-action">
        <v-spacer></v-spacer>
        <v-btn color="gray" class="dialog-Button" @click="close">閉じる</v-btn>
      </v-card-actions>
    </v-card>

    <!-- 完了確認ダイアログ -->
    <v-confirm-dialog
      ref="completeConfirm"
      title="確認"
      message="ダウンロードが完了しました。このモーダルを閉じますか？"
      buttonMessage="はい"
      cancelButtonMessage="いいえ"
      @confirm="close"
    />
  </v-dialog>
</template>

<script>
import VConfirmDialog from '@/components/atoms/ConfirmDialog'

export default {
  components: {
    VConfirmDialog,
  },

  data: () => ({
    show: false,
    sheetId: null,  // History.vueから設定されるsheet_id
    sheetContentPagesData: null,  // History.vueから設定されるsheet_content_pages
    subjectCode: null,  // History.vueから設定される教科コード
    loadingStates: {
      audio: {},  // { [content.number]: true/false }
      script: {}  // { [content.number]: true/false }
    },
    filesData: {
      audio: {},  // { [content.number]: [{index: 1}, {index: 2}] }
      script: {}  // { [content.number]: [{index: 0}, {index: 1}] }
    },
    modalType: 'registExam',  // registExam: テスト作成完了画面からのダウンロード, history: 申し込み履歴画面からのダウンロード
  }),

  computed: {
    sheetContentPages() {
      // History.vueから渡されたデータを優先、なければstoreから取得
      return this.sheetContentPagesData || this.$store.state.sheet?.sheetContentPages?.sheet_content_pages || []
    },
    isKokugoOrEigo() {
      // History.vueから渡された教科コードを優先、なければstoreから取得
      const subjectCode = this.subjectCode || this.$store.state.top.selectedSubject.code
      // 国語: id=1, 英語: id=3
      return subjectCode === 'ko' || subjectCode === 'ei'
    }
  },

  methods: {
    async open() {
      this.show = true
      // モーダルを開いたときにファイル存在チェックを実行
      await this.checkAllFiles()
    },

    close() {
      this.show = false
      // モーダルを閉じるときに状態をリセット
      this.loadingStates = { audio: {}, script: {} }
      this.filesData = { audio: {}, script: {} }
      this.sheetId = null
      this.sheetContentPagesData = null
      this.subjectCode = null
      this.modalType = 'registExam'
    },

    setSheetId(sheetId) {
      this.sheetId = sheetId
      this.modalType = 'history'
    },

    setSheetContentPages(sheetContentPages) {
      this.sheetContentPagesData = sheetContentPages
    },

    setSubjectCode(subjectCode) {
      this.subjectCode = subjectCode
    },

    getSheetId() {
      // History.vueから設定されたsheetIdを優先、なければstoreから取得
      return this.sheetId || this.$store.state.sheet?.sheetContentPages?.id
    },

    async checkAllFiles() {
      // 国語または英語の場合のみチェック
      if (!this.isKokugoOrEigo) return

      const sheetId = this.getSheetId()
      if (!sheetId) return

      try {
        // ローディング状態を設定
        this.sheetContentPages.forEach(content => {
          this.$set(this.loadingStates.audio, content.number, true)
          this.$set(this.loadingStates.script, content.number, true)
        })

        // APIを呼び出して全ファイルの存在チェック
        const response = await this.$axios.get(`/api/v1/teacher/sheets/${sheetId}/check_files`)

        // レスポンスを元に存在状態を更新
        response.data.forEach(item => {
          this.$set(this.filesData.audio, item.number, item.audio_files || [])
          this.$set(this.filesData.script, item.number, item.script_files || [])
          this.$set(this.loadingStates.audio, item.number, false)
          this.$set(this.loadingStates.script, item.number, false)
        })
      } catch (error) {
        console.error('ファイル存在チェックエラー', error)
        // エラー時は全て空配列に設定
        this.sheetContentPages.forEach(content => {
          this.$set(this.filesData.audio, content.number, [])
          this.$set(this.filesData.script, content.number, [])
          this.$set(this.loadingStates.audio, content.number, false)
          this.$set(this.loadingStates.script, content.number, false)
        })
      }
    },

    isLoadingAudio(content) {
      return this.loadingStates.audio[content.number] === true
    },

    isLoadingScript(content) {
      return this.loadingStates.script[content.number] === true
    },

    getAudioFiles(content) {
      return this.filesData.audio[content.number] || []
    },

    getScriptFiles(content) {
      return this.filesData.script[content.number] || []
    },

    async downloadQuestionWord() {
      const sheetId = this.getSheetId()
      if (!sheetId) {
        console.error('シートIDが取得できません')
        return
      }

      try {
        // 組版APIを先に呼ぶ
        if (this.modalType === 'registExam') {
          await this.compose(1)
        }

        // ダウンロードAPI呼び出し
        const response = await this.$axios.get(`/api/v1/teacher/sheets/${sheetId}/download_question_word`, {
          responseType: 'blob'
        })

        // ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = this.getFilenameFromContentDisposition(response.headers['content-disposition']) || '問題用紙.docx'
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('問題用紙Wordダウンロードエラー', error)
        alert('問題用紙Wordのダウンロードに失敗しました')
      }
    },

    async downloadAnswerSheetExcel() {
      const sheetId = this.getSheetId()
      if (!sheetId) {
        console.error('シートIDが取得できません')
        return
      }

      try {
        // 組版APIを先に呼ぶ
        if (this.modalType === 'registExam') {
          await this.compose(2)
        }

        // ダウンロードAPI呼び出し
        const response = await this.$axios.get(`/api/v1/teacher/sheets/${sheetId}/download_answer_sheet_excel`, {
          responseType: 'blob'
        })

        // ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = this.getFilenameFromContentDisposition(response.headers['content-disposition']) || '解答用紙.xlsx'
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('解答用紙Excelダウンロードエラー', error)
        alert('解答用紙Excelのダウンロードに失敗しました')
      }
    },

    async downloadAnswerExcel() {
      const sheetId = this.getSheetId()
      if (!sheetId) {
        console.error('シートIDが取得できません')
        return
      }

      try {
        // 組版APIを先に呼ぶ
        if (this.modalType === 'registExam') {
          await this.compose(2)
        }

        // ダウンロードAPI呼び出し
        const response = await this.$axios.get(`/api/v1/teacher/sheets/${sheetId}/download_answer_excel`, {
          responseType: 'blob'
        })

        // ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = this.getFilenameFromContentDisposition(response.headers['content-disposition']) || '解答.xlsx'
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('解答Excelダウンロードエラー', error)
        alert('解答Excelのダウンロードに失敗しました')
      }
    },

    async compose (outputType) {
      this.$app.$refs.loading.open({type: 'print', message: 'ファイル生成中'})
      const sheetId = this.getSheetId()
      const params = {
        Title: this.$store.state.sheet.title,
        SheetId: sheetId,
        OutputType: outputType,
        Subject: this.$store.state.top.selectedSubject.code,
        QuestionSheetPrintOption: {
          FrontCover: this.$store.state.sheet.printType === 'booklet',
        },
        AnswerSheetPrintOption: {},
        Question: this.$store.state.sheet.usedItems.map(sheetContentPage => {
          return {                                                        //大問内容の配列
            ContentsCode: sheetContentPage.contentCode,
            Point: sheetContentPage.sheetQuestions.reduce((total, sheetQuestion) => {
              return total += sheetQuestion.allot * sheetQuestion.question.question_cnt
            }, 0),
            Question: sheetContentPage.sheetQuestions.map(sheetQuestion => {
              return {
                AspectType: sheetQuestion.pointId,
                Point: sheetQuestion.allot,
                AnswerCount: sheetQuestion.question.question_cnt,
              }
            })
          }
        }),
      }
      await this.$axios.post('/api/composition_execute', params)
      this.$app.$refs.loading.close({})
    },

    async downloadAudioFile(content, index) {
      if (!content.id) {
        console.error('コンテンツIDが取得できません')
        return
      }

      try {
        const response = await this.$axios.get(`/api/v1/teacher/contents/${content.id}/download_audio`, {
          params: { index },
          responseType: 'blob'
        })

        // ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = this.getFilenameFromContentDisposition(response.headers['content-disposition']) || `音声データ_大問${content.number}-${index}.mp3`
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('音声ファイルダウンロードエラー', error)
        alert('音声ファイルのダウンロードに失敗しました')
      }
    },

    async downloadScriptImage(content, index) {
      if (!content.id) {
        console.error('コンテンツIDが取得できません')
        return
      }

      try {
        const response = await this.$axios.get(`/api/v1/teacher/contents/${content.id}/download_script`, {
          params: { index },
          responseType: 'blob'
        })

        // ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = this.getFilenameFromContentDisposition(response.headers['content-disposition']) || `スクリプト画像_大問${content.number}-${index + 1}.png`
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('スクリプト画像ダウンロードエラー', error)
        alert('スクリプト画像のダウンロードに失敗しました')
      }
    },

    openCompleteDownloadModal() {
      this.$refs.completeConfirm.open()
    },

    getFilenameFromContentDisposition(contentDisposition) {
      if (!contentDisposition) return null

      // filename*=UTF-8''encoded-filename 形式を優先的に使用
      const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i)
      if (filenameStarMatch) {
        return decodeURIComponent(filenameStarMatch[1])
      }

      // filename="filename" 形式をフォールバック
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/i)
      if (filenameMatch) {
        return filenameMatch[1]
      }

      return null
    }
  }
}
</script>

<style lang="stylus" scoped>
.title
  justify-content center

.dialog-Button
  font-weight normal

.th_top
  border-top 1px solid rgba(0, 0, 0, 0.12)

.td_top
  border-top 1px solid rgba(0, 0, 0, 0.12)
</style>
